// Display-only cleanup. Canonical instrument names and chart identifiers stay on the original rows.
const CLASS = "(?:(?:Class|Series) [A-Z0-9-]+\\s+)?";
const EXPLICIT_SECURITY = CLASS + "(?:Common (?:Stock|Shares?)(?: of Beneficial Interest)?|Ordinary (?:Stock|Shares?)|Preferred (?:Stock|Shares?)|Capital Stock|American Depositary (?:Shares?|Receipts?)|Depositary Shares?|Shares of Beneficial Interest)";
// These tails describe the security only after an explicit security phrase.
const SECURITY_TAIL = "(?:\\s+When[- ]Issued|\\s+\\([^()]{1,100}\\)|\\s+\\$?\\d+(?:\\.\\d+)?\\s+par value(?: per share)?)*(?:,?\\s+(?:Each\\s+)?representing\\b[\\s\\S]*)?";
const EXPLICIT_SUFFIX = new RegExp("(?:\\s*[-–—,;]\\s*|\\s+)" + EXPLICIT_SECURITY + SECURITY_TAIL + "$|\\s*\\(" + EXPLICIT_SECURITY + "\\)$", "i");
const SEPARATED_SUFFIX = /\s+[-–—]\s+(?:Units|Warrants|Rights|ADR|ADS|ETF|Class [A-Z0-9-]+|Series [A-Z0-9-]+)$/i;
const SHORTHAND_SUFFIX = /\s+(Units|ADR|ADS|ETF)$/i;
const COMPANY_CONTEXT = /\b(?:Inc(?:orporated)?|Corp(?:oration)?|Company|Co|Limited|Ltd|Plc|L\.?P\.?|S\.?A\.?|N\.?V\.?|A\.?G\.?)\.?(?=\s|$)/i;

export function displayCompanyName(value) {
  const original = typeof value === "string" ? value.trim() : "";
  let display = original;
  while (display) {
    let shorter = display.replace(EXPLICIT_SUFFIX, "").replace(SEPARATED_SUFFIX, "");
    const shorthand = SHORTHAND_SUFFIX.exec(shorter);
    if (shorthand && (shorthand[1].toUpperCase() === "ETF" || COMPANY_CONTEXT.test(shorter.slice(0, shorthand.index)))) {
      shorter = shorter.slice(0, shorthand.index);
    }
    if (shorter === display) break;
    const cleaned = shorter.replace(/[\s,;–—-]+$/, "").trim();
    if (!cleaned) break;
    display = cleaned;
  }
  return display;
}

export function earningsTimePresentation(reportTime, reportTimeZone) {
  if (reportTime === "BeforeMarketOpen") return { kind: "sun", label: "Before market open" };
  if (reportTime === "AfterMarketClose") return { kind: "moon", label: "After market close" };
  if (reportTime === "DuringMarket") return { kind: "clock", label: "During market" };
  if (reportTime === "AtMarketClose") return { kind: "clock", label: "At market close" };
  if (/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(reportTime || "") && reportTimeZone) {
    return { kind: "clock", label: `${reportTime} ${reportTimeZone}` };
  }
  return { kind: "unknown", label: "Report time unspecified" };
}

export function splitRatioLabel(item) {
  return normalizeSplitRatio(item).label;
}

// Reduce the supplied decimal numbers exactly, including scientific notation.
// This preserves their existing precision; it does not infer a more precise provider ratio.
export function normalizeSplitRatio(item) {
  const [newNumerator, newDenominator] = decimalFraction(item.numerator);
  const [oldNumerator, oldDenominator] = decimalFraction(item.denominator);
  const numerator = newNumerator * oldDenominator;
  const denominator = newDenominator * oldNumerator;
  let left = numerator; let right = denominator;
  while (right !== 0n) { const remainder = left % right; left = right; right = remainder; }
  const newShares = (numerator / left).toString();
  const oldShares = (denominator / left).toString();
  return { numerator: newShares, denominator: oldShares, label: `${newShares}:${oldShares}` };
}

function decimalFraction(value) {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) throw new RangeError("Split ratio values must be finite positive numbers.");
  const [coefficient, exponent = "0"] = String(value).toLowerCase().split("e");
  const [whole, fraction = ""] = coefficient.split(".");
  const digits = BigInt(whole + fraction);
  const scale = fraction.length - Number(exponent);
  return scale >= 0 ? [digits, 10n ** BigInt(scale)] : [digits * 10n ** BigInt(-scale), 1n];
}
